import { utils as coreUtils } from '@adc/ui-core'
import type {
  ClosingMethod,
  ReoOffer,
  TrusteeContract,
  TransactionCoordinatorClosings,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'

const { formatTitleCase } = coreUtils.formatters

export const GROUP_LABEL: Record<string, string> = {
  TRANSACTION_COORDINATOR: 'Transaction Coordinator',
}
export const getInitials = (name: string, maxInitials?: number): string => {
  if (!name) return ''
  const words = name.split(' ')
  const initials = words
    .map(word => word[0]?.toUpperCase() || '')
    .filter(Boolean)

  if (maxInitials && maxInitials > 0) {
    return initials.slice(0, maxInitials).join('')
  }

  return initials.join('')
}

export const ROLE_DISPLAY_LABEL: Record<string, string> = {
  [graph.enums.ClosingRelationship.TransactionCoordinator]:
    'Transaction Specialist',
}

const createAssociatedParty = (id: string, name: string, role: string) => ({
  id,
  name,
  role,
  displayLabel: ROLE_DISPLAY_LABEL[role] ?? formatTitleCase(role),
})

export const mapClosingMethodData = (
  closingMethod: ClosingMethod | TransactionCoordinatorClosings
) => {
  const { __typename } = closingMethod
  const listing = closingMethod.listing
  const address = listing.formatted_address
  const baseData = {
    address,
    debugInfo: {
      listingId: listing.listing_id,
      __typename,
    },
  }

  const associatedParties = []

  const assetManager = listing.parties?.find(
    party => party?.party_role === graph.enums.ListingPartyRole.AssetManager
  )

  if (assetManager?.party_name) {
    associatedParties.push(
      // @ts-ignore next
      createAssociatedParty(
        assetManager.party_id ??
          `${graph.enums.ListingPartyRole.AssetManager}-unknown`,
        assetManager.party_name,
        graph.enums.ListingPartyRole.AssetManager
      )
    )
  }

  const transactionSpecialist = closingMethod.closing_associations?.find(
    association =>
      association.closing_relationship ===
      graph.enums.ClosingRelationship.TransactionCoordinator
  )?.party

  if (transactionSpecialist?.person) {
    const { first_name, last_name } = transactionSpecialist.person
    const fullName = [first_name, last_name].filter(Boolean).join(' ')

    if (fullName) {
      associatedParties.push(
        // @ts-ignore next
        createAssociatedParty(
          transactionSpecialist.party_id ??
            `${graph.enums.ClosingRelationship.TransactionCoordinator}-unknown`,
          fullName,
          graph.enums.ClosingRelationship.TransactionCoordinator
        )
      )
    }
  }

  if (__typename === 'ReoOffer') {
    const reoOffer = closingMethod as ReoOffer

    return {
      ...baseData,
      debugInfo: {
        ...baseData.debugInfo,
        offerOrTrusteeId: reoOffer.offer_id,
      },
      soldOn: reoOffer.sold_time,
      soldAmount: reoOffer.winning_bid_amount,
      associatedParties,
      closingStatus: reoOffer.workflow?.current_status_label ?? 'Unknown',
    }
  } else if (__typename === 'TrusteeContract') {
    const trusteeContract = closingMethod as TrusteeContract
    return {
      ...baseData,
      debugInfo: {
        ...baseData.debugInfo,
        offerOrTrusteeId: trusteeContract.trustee_contract_id,
      },
      soldOn: trusteeContract.closed_date,
      soldAmount: trusteeContract.winning_bid_amount,
      associatedParties,
      closingStatus: trusteeContract.record_status,
    }
  } else if (__typename === 'TransactionCoordinatorClosings') {
    const transactionCoordinatorClosing =
      closingMethod as TransactionCoordinatorClosings
    return {
      ...baseData,
      associatedParties,
      debugInfo: {
        ...baseData.debugInfo,
        offerOrTrusteeId: transactionCoordinatorClosing.closing_resource_id,
      },
      soldOn: closingMethod.sold_date,
      soldAmount: closingMethod.sold_amount,
      closingStatus: closingMethod.closing_status,
    }
  }

  throw new Error(`Unknown closing method type: ${__typename}`)
}
